import { useMemo, useState } from "react"
import { toast } from "react-toastify"
import { Dialog } from "~/ui-rtk/components/ui/common"
import { Button } from "~/ui-rtk/components/ui/controls"
import FileUpload from "~/ui-rtk/components/ui/controls/FileUpload"
import { H3, P } from "~/ui-rtk/components/ui/typography"
import {
  useImportTagTaxonomyMutation,
  type TaxonomyImportAction,
  type TaxonomyImportCategoryPlan,
  type TaxonomyImportResult,
} from "~/ui-rtk/api/tagCategoriesApi"
import { cn } from "~/ui-rtk/utils/tailwind-utils"

/**
 * Two-step CSV import for the tag taxonomy: pick the sheet, see exactly what
 * will be created and updated (a server-side dry run), then apply.
 *
 * The sheet is the format brands already keep — one row per value with
 * `Category, Category Definition, tag value, tag value Definition` — so the
 * definitions written for humans become the descriptions that also steer the
 * AI tag suggester. Existing categories/values are matched by slug and only
 * their label/description change; nothing is ever deleted, which is why the
 * preview can afford to be the only confirmation.
 */

interface ImportTaxonomyDialogProps {
  isOpen: boolean
  onClose: () => void
}

// The shared Dialog renders a bare panel; the shell (surface, border,
// padding) is the caller's, same as the other modals on this app.
const DIALOG_CONTAINER_CLS =
  "flex max-h-[90vh] w-full max-w-[95vw] flex-col gap-4 rounded-lg border border-basic-blue bg-basic-dark-blue p-5 text-left text-white shadow-xl"

const ACTION_LABEL: Record<TaxonomyImportAction, string> = {
  create: "new",
  update: "update",
  unchanged: "no change",
}

const ACTION_CLASS: Record<TaxonomyImportAction, string> = {
  create: "text-system-sea-400",
  update: "text-system-yellow-300",
  unchanged: "text-system-gray-500",
}

function ActionBadge({ action }: { action: TaxonomyImportAction }) {
  return (
    <span
      className={cn(
        "text-xs font-semibold uppercase tracking-wide",
        ACTION_CLASS[action],
      )}
    >
      {ACTION_LABEL[action]}
    </span>
  )
}

/** "3 new · 2 updated · 4 unchanged" for one category's values. */
function describeOptions(category: TaxonomyImportCategoryPlan): string {
  if (category.valueType === "free_text") return "free text — no value list"
  const counts = { create: 0, update: 0, unchanged: 0 }
  for (const option of category.options) counts[option.action] += 1
  const parts = [
    counts.create ? `${counts.create} new` : null,
    counts.update ? `${counts.update} updated` : null,
    counts.unchanged ? `${counts.unchanged} unchanged` : null,
  ].filter(Boolean)
  return parts.length ? parts.join(" · ") : "no values"
}

/** Values in the sheet that need a write, named — so the preview is checkable. */
function changedOptionLabels(category: TaxonomyImportCategoryPlan): string {
  return category.options
    .filter(option => option.action !== "unchanged")
    .map(option =>
      option.action === "create"
        ? option.label
        : `${option.label} (${option.changes.join(", ")})`,
    )
    .join(", ")
}

const totalWrites = (result: TaxonomyImportResult): number =>
  result.summary.categoriesCreated +
  result.summary.categoriesUpdated +
  result.summary.optionsCreated +
  result.summary.optionsUpdated

const errorMessage = (error: unknown, fallback: string): string =>
  (error as { data?: { message?: string } })?.data?.message ??
  (error instanceof Error ? error.message : fallback)

export default function ImportTaxonomyDialog({
  isOpen,
  onClose,
}: ImportTaxonomyDialogProps) {
  const [importTaxonomy] = useImportTagTaxonomyMutation()
  const [fileName, setFileName] = useState<string | null>(null)
  const [csv, setCsv] = useState<string | null>(null)
  const [plan, setPlan] = useState<TaxonomyImportResult | null>(null)
  const [isPreviewing, setIsPreviewing] = useState(false)
  const [isImporting, setIsImporting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const writes = useMemo(() => (plan ? totalWrites(plan) : 0), [plan])

  const reset = () => {
    setFileName(null)
    setCsv(null)
    setPlan(null)
    setError(null)
  }

  const handleClose = () => {
    if (isImporting) return
    reset()
    onClose()
  }

  const handleFiles = async (files: FileList | null) => {
    const file = files?.[0]
    if (!file) return
    setError(null)
    setPlan(null)
    setFileName(file.name)
    setIsPreviewing(true)
    try {
      const text = await file.text()
      setCsv(text)
      const preview = await importTaxonomy({ csv: text, dryRun: true }).unwrap()
      setPlan(preview)
    } catch (err) {
      setCsv(null)
      setError(errorMessage(err, "Could not read that CSV"))
    } finally {
      setIsPreviewing(false)
    }
  }

  const handleImport = async () => {
    if (!csv || isImporting) return
    setIsImporting(true)
    try {
      const result = await importTaxonomy({ csv }).unwrap()
      const { summary } = result
      toast.success(
        `Imported: ${summary.categoriesCreated} new categories, ${summary.categoriesUpdated} updated · ${summary.optionsCreated} new values, ${summary.optionsUpdated} updated`,
      )
      reset()
      onClose()
    } catch (err) {
      setError(errorMessage(err, "Import failed"))
    } finally {
      setIsImporting(false)
    }
  }

  return (
    <Dialog
      isOpen={isOpen}
      handleClose={handleClose}
      width={760}
      classes={{ container: DIALOG_CONTAINER_CLS }}
    >
      <div className="flex flex-col gap-4 min-h-0">
        <div>
          <H3 className="text-white font-bold text-lg">
            Import taxonomy from CSV
          </H3>
          <P className="text-sm text-system-gray-400 mt-1">
            One row per value, with the columns{" "}
            <code className="text-system-gray-300">
              Category, Category Definition, tag value, tag value Definition
            </code>
            . The category may be blank on continuation rows. Definitions become
            descriptions, a <code>(free text)</code> value makes a free-text
            category, and rows for Style, Length or Persona update the built-in
            categories of the same name.
          </P>
        </div>

        <div className="flex items-start gap-4">
          <FileUpload
            accept=".csv,text/csv"
            maxFiles={1}
            // Matches the server's 2 MB body cap so an oversized sheet is
            // refused here with the picker's own message, not a 400.
            maxSize={2}
            text={plan ? "Choose another file" : "Choose CSV file"}
            onUpload={files => void handleFiles(files)}
          />
          {fileName && (
            <P className="text-sm text-system-gray-300 pt-2.5 truncate">
              {fileName}
            </P>
          )}
        </div>

        {error && <P className="text-sm text-system-red-400">{error}</P>}

        {isPreviewing && (
          <P className="text-sm text-system-gray-400">Reading the sheet…</P>
        )}

        {plan && (
          <div className="flex flex-col gap-3 min-h-0">
            <P className="text-sm text-system-gray-300">
              <strong className="text-white">
                {plan.summary.categoriesCreated}
              </strong>{" "}
              new categories,{" "}
              <strong className="text-white">
                {plan.summary.categoriesUpdated}
              </strong>{" "}
              updated,{" "}
              <strong className="text-white">
                {plan.summary.categoriesUnchanged}
              </strong>{" "}
              unchanged ·{" "}
              <strong className="text-white">
                {plan.summary.optionsCreated}
              </strong>{" "}
              new values,{" "}
              <strong className="text-white">
                {plan.summary.optionsUpdated}
              </strong>{" "}
              updated,{" "}
              <strong className="text-white">
                {plan.summary.optionsUnchanged}
              </strong>{" "}
              unchanged
            </P>

            <div className="overflow-y-auto max-h-[45vh] rounded-lg border border-system-blue-700">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-system-blue-800 text-left text-xs uppercase tracking-wide text-system-gray-400">
                  <tr>
                    <th className="px-3 py-2">Category</th>
                    <th className="px-3 py-2">Action</th>
                    <th className="px-3 py-2">Values</th>
                  </tr>
                </thead>
                <tbody>
                  {plan.categories.map(category => {
                    const changed = changedOptionLabels(category)
                    return (
                      <tr
                        key={category.slug}
                        className="border-t border-system-blue-700 align-top"
                      >
                        <td className="px-3 py-2">
                          <div className="text-white font-semibold">
                            {category.label}
                          </div>
                          <div className="text-xs text-system-gray-500">
                            {category.slug}
                            {category.isSystem && (
                              <span className="text-system-sea-500">
                                {" "}
                                · system
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <ActionBadge action={category.action} />
                          {category.changes.length > 0 && (
                            <div className="text-xs text-system-gray-500">
                              {category.changes.join(", ")}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <div className="text-system-gray-300">
                            {describeOptions(category)}
                          </div>
                          {changed && (
                            <div className="text-xs text-system-gray-500 mt-0.5">
                              {changed}
                            </div>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {plan.warnings.length > 0 && (
              <div className="rounded-lg bg-system-blue-800/40 px-3 py-2">
                <P className="text-xs font-semibold text-system-yellow-300 mb-1">
                  Skipped or merged rows
                </P>
                <ul className="list-disc pl-4 text-xs text-system-gray-300">
                  {plan.warnings.map(warning => (
                    <li key={warning}>{warning}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 mt-1">
          <Button
            variant={{ variant: "outlined" }}
            size="sm"
            type="button"
            onClick={handleClose}
            disabled={isImporting}
          >
            Cancel
          </Button>
          <Button
            variant={{ variant: "solid", color: "pink" }}
            size="sm"
            type="button"
            isLoading={isImporting}
            disabled={!plan || writes === 0 || isImporting || isPreviewing}
            onClick={() => void handleImport()}
          >
            {plan && writes === 0
              ? "Nothing to import"
              : `Import${writes ? ` ${writes} changes` : ""}`}
          </Button>
        </div>
      </div>
    </Dialog>
  )
}
